// News visuals: thumbnail, meta row, cards (card / row / feature) and wire row.
import i18n, { labels } from '@/i18n';
import { useState } from 'react';
import { Link } from 'react-router';
import { ArrowChip } from '@/components/ui/Buttons';
import { Icon } from '@/components/ui/Icon';
import { Importance } from '@/components/ui/Indicators';
import { cx } from '@/lib/format';
import { useTime } from '@/hooks/useTime';
import type { NewsItem } from '@/types';

export const CAT_LABEL = labels('news:cat', ['all', 'crypto', 'stocks', 'macro', 'world'] as const);
export const CAT_SHORT = labels('news:catShort', ['crypto', 'stocks', 'macro', 'world'] as const);
export const CAT_ICON = { crypto: 'price', stocks: 'ws', macro: 'macro', world: 'radar' } as const;
/** «3 sources · 1 duplicate hidden» when Orca counted them, otherwise the publisher's name */
export function srcLine(n: NewsItem) {
  if (n.sources === undefined) return n.source.name;
  return `${i18n.t('common:unit.source', { count: n.sources })}${n.dupes ? ` · ${i18n.t('news:dupesHidden', { n: n.dupes })}` : ''}`;
}

/** newest first */
export const byTime = (a: NewsItem, b: NewsItem) => b.at.localeCompare(a.at);

/** The article's own picture (lazy, no referrer); without one — or when it fails
 *  to load — ORCA's category fallback, which never passes for the article's photo. */
export function NewsImage({ n, className, label = false }: { n: NewsItem; className: string; label?: boolean }) {
  const [failed, setFailed] = useState<string | null>(null);
  if (n.image && failed !== n.image) {
    return <img className={className} src={n.image} alt="" loading="lazy" decoding="async" referrerPolicy="no-referrer" onError={() => setFailed(n.image)} />;
  }
  return (
    <span className={cx(className, 'nt-fb', n.category)} data-fallback="">
      <Icon name={CAT_ICON[n.category]} />
      {label && <small>{n.source.name}</small>}
    </span>
  );
}

export function NewsThumb({ n, big = false, small = false }: { n: NewsItem; big?: boolean; small?: boolean }) {
  return (
    <div className={cx('nthumb', n.category, big && 'big', small && 'small')}>
      <NewsImage n={n} className="nt-img" label={!small} />
      <span className="nt-ic"><Icon name={CAT_ICON[n.category]} /></span>
      <span className="nt-coins">{n.tags.slice(0, small ? 1 : 2).map((t) => <b key={t}>{t}</b>)}</span>
      {n.isNew && <em className="nt-new">{i18n.t('news:new')}</em>}
    </div>
  );
}

/** `withDay`: lists that span several days show the day for older items */
export function NewsMeta({ n, withDay }: { n: NewsItem; withDay?: boolean }) {
  const tf = useTime();
  return (
    <div className="nc-meta">
      <span className={cx('wi-k', n.category)}>{CAT_SHORT[n.category]}</span>
      <time dateTime={n.at}>{withDay ? tf.stamp(n.at) : tf.time(n.at)}</time>
      {n.imp ? <Importance n={n.imp} /> : null}
    </div>
  );
}

export function NewsFoot({ n }: { n: NewsItem }) {
  return (
    <div className="nc-foot">
      {n.tags.map((t) => <span key={t} className="tag">{t}</span>)}
      <span className="nc-src">{srcLine(n)}</span>
    </div>
  );
}

type Mode = 'card' | 'row' | 'feature';
export function NewsCard({ n, mode = 'card', onOpen }: { n: NewsItem; mode?: Mode; onOpen: (id: number) => void }) {
  const open = () => onOpen(n.id);
  if (mode === 'feature') {
    return (
      <button type="button" className="nfeat" onClick={open}>
        <NewsThumb n={n} big />
        <div className="nf-b">
          <NewsMeta n={n} />
          <p className="nf-t">{n.title}</p>
          <p className="nf-d">{n.dek}</p>
          <NewsFoot n={n} />
          <span className="nf-open">{i18n.t('news:openPreview')} <ArrowChip label={i18n.t('news:read')} /></span>
        </div>
      </button>
    );
  }
  if (mode === 'row') {
    return (
      <button type="button" className="nrow" onClick={open}>
        <NewsThumb n={n} small />
        <div className="nr-b"><NewsMeta n={n} /><p className="nr-t">{n.title}</p><p className="nr-d">{n.dek}</p><NewsFoot n={n} /></div>
      </button>
    );
  }
  return (
    <button type="button" className="ncard" onClick={open}>
      <NewsThumb n={n} small />
      <div className="nc-b">
        <NewsMeta n={n} />
        <p className="nc-t">{n.title}</p>
        <span className="nc-src">{[n.tags.join(' · '), srcLine(n)].filter(Boolean).join(' · ')}</span>
      </div>
    </button>
  );
}

/** Compact wire row (portfolio "Новости по портфелю"). */
export function WireRow({ n }: { n: NewsItem }) {
  const tf = useTime();
  return (
    <Link className="wr" to={`/news?id=${n.id}`}>
      <time dateTime={n.at}>{tf.stamp(n.at)}</time>
      <div className="wr-main">
        <div className="wr-meta"><span className={cx('wi-k', n.category)}>{CAT_SHORT[n.category]}</span>{n.tags.map((t) => <span key={t} className="tag">{t}</span>)}</div>
        <p className="wr-title">{n.title}</p>
        <span className="wr-src">{srcLine(n)}</span>
      </div>
      {n.imp ? <Importance n={n.imp} /> : null}
    </Link>
  );
}
